// The picture of a four-sided shape with one or both of its diagonals, with
// letters at its corners and where the diagonals cross, small marks, arrows,
// arcs, right-angle squares and labels. Plain React SVG, like
// practice-shape.tsx.

import { PairText } from './practice-shape-pair';
import { NOTE_SIZE, arcPath, pathThrough } from './practice-shape-pair-data';
import {
  describeQuadDiagonals,
  quadDiagonalsPicture,
  type QuadDiagonalsSpec,
} from './practice-quad-diagonals-data';

const INK = '#2d4a3e';
const SHAPE_FILL = '#eef3ee';

const fixed = (value: number) => value.toFixed(2);

export function QuadDiagonalsView({ quad }: { quad: QuadDiagonalsSpec }) {
  const picture = quadDiagonalsPicture(quad);
  const { box } = picture;

  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeQuadDiagonals(quad)}
        viewBox={`${fixed(box.x)} ${fixed(box.y)} ${fixed(box.width)} ${fixed(box.height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(box.width * 1.4))}px` }}
      >
        <path
          data-outline="true"
          d={pathThrough(picture.outline, true)}
          fill={SHAPE_FILL}
          stroke={INK}
          strokeWidth="2.2"
          strokeLinejoin="round"
        />
        {picture.diagonals.map((line, index) => (
          <line
            key={index}
            data-part="diagonal"
            x1={fixed(line.from.x)}
            y1={fixed(line.from.y)}
            x2={fixed(line.to.x)}
            y2={fixed(line.to.y)}
            stroke={INK}
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        ))}
        {picture.arcs.map((arc, index) => (
          <path
            key={index}
            data-mark="arc"
            d={arcPath(arc)}
            fill="none"
            stroke={INK}
            strokeWidth="1.5"
          />
        ))}
        {picture.squares.map((square, index) => (
          <path
            key={index}
            data-mark="square"
            d={pathThrough(square, false)}
            fill="none"
            stroke={INK}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        ))}
        {picture.ticks.map((tick, index) => (
          <line
            key={index}
            data-mark="tick"
            x1={fixed(tick.from.x)}
            y1={fixed(tick.from.y)}
            x2={fixed(tick.to.x)}
            y2={fixed(tick.to.y)}
            stroke={INK}
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        ))}
        {picture.arrows.map((arrow, index) => (
          <path
            key={index}
            data-mark="arrow"
            d={pathThrough(arrow, false)}
            fill="none"
            stroke={INK}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        {picture.dot ? (
          <circle
            data-part="crossing"
            cx={fixed(picture.dot.x)}
            cy={fixed(picture.dot.y)}
            r="2.6"
            fill={INK}
          />
        ) : null}
        {picture.letters.map((label, index) => (
          <PairText key={index} label={label} where="corner" />
        ))}
        {picture.sideLabels.map((label, index) => (
          <PairText key={index} label={label} where="side" />
        ))}
        {picture.diagonalLabels.map((label, index) => (
          <PairText key={index} label={label} where="diagonal" />
        ))}
        {picture.halfLabels.map((label, index) => (
          <PairText key={index} label={label} where="half" />
        ))}
        {picture.angleLabels.map((label, index) => (
          <PairText key={index} label={label} where="angle" />
        ))}
        {picture.note ? (
          <PairText
            label={picture.note}
            where="note"
            size={NOTE_SIZE}
            bold={false}
          />
        ) : null}
      </svg>
    </figure>
  );
}
